import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { createLoadOptions } from "../../hooks/useDebounce";
import { useI18n } from "../../hooks/useTranslations";
import { useCarDriversApi } from "../../http/carDrivers/api";
import { useAdminCarsApi } from "../../http/cars/api";
import { Car, CarNoCarDepency } from "../../http/cars/types";
import { useCarSubsidiariesApi } from "../../http/carSubsidiaries/api";
import { Fine, FineStatusType, getFineStatuses } from "../../http/fines/types";
import { useSubsidiariesApi } from "../../http/subsidiaries/api";
import { Subsidiary } from "../../http/subsidiaries/types";
import { useUsersApi } from "../../http/users/api";
import { User } from "../../http/users/types";
import { Currency, getCurrencies } from "../../types";
import { formatDateNumeric } from "../../types/utils";
import Button from "../ui/Button";
import CostInput from "../ui/CostInput";
import DatePicker from "../../ui/DatePicker";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormGrid from "../ui/FormGrid";
import Input from "../ui/Input";
import Numeric from "../ui/Numeric";
import reactSelectStyles from "../ui/ReactSelectUtils";
import Textarea from "../ui/Textarea";

const CAR_FIELDS = ["id", "vehicleIdentificationNumber", "currentPlateNumber", "lastPlateNumber"] as const;
type CarField = (typeof CAR_FIELDS)[number];
type CarSearch = Pick<CarNoCarDepency, CarField>;

const USER_FIELDS = ["id", "fullName"] as const;
type UserField = (typeof USER_FIELDS)[number];
type UserSearch = Pick<User, UserField>;

// periods in which the car had a driver and a subsidiary: used to propose the ones of the violation date
const DRIVER_PERIOD_FIELDS = ["driver", "startDate", "endDate"] as const;
const SUBSIDIARY_PERIOD_FIELDS = ["subsidiary", "startDate", "endDate"] as const;

const SUBSIDIARY_FIELDS = ["id", "name"] as const;
type SubsidiaryField = (typeof SUBSIDIARY_FIELDS)[number];
type SubsidiarySearch = Pick<Subsidiary, SubsidiaryField>;

export type FineFormBody = {
  car?: Car;
  driver?: User;
  subsidiary?: Subsidiary;
  status: FineStatusType;
  verbalNumber: string;
  violationDate: string;
  communicationDate?: string;
  amount?: number | null;
  amountCurrency?: Currency | null;
  points?: number | null;
  accountant?: User | null;
  hrReferent?: User | null;
  note?: string;
};

type FineFormProps = {
  isDisabled?: boolean;
  isLoading?: boolean;
  onSubmit: (formData: FineFormBody) => Promise<void>;
  fine?: Fine | null;
  carId?: string | null;
};

const FineForm: FC<FineFormProps> = ({ fine, onSubmit, isDisabled, isLoading, carId }: FineFormProps) => {
  const i18n = useI18n();
  const carsApi = useAdminCarsApi();
  const subsidiariesApi = useSubsidiariesApi();
  const usersApi = useUsersApi();
  const navigate = useNavigate();
  const aborter = useRef(new AbortController());
  const dispatch = useContext(NotificationDispatch);
  const currencies = getCurrencies();
  const [defaultCarOptions, setDefaultCarOptions] = useState<CarSearch[]>([]);
  const [defaultUserOptions, setDefaultUserOptions] = useState<UserSearch[]>([]);
  const [defaultSubsidiaryOptions, setDefaultSubsidiaryOptions] = useState<SubsidiarySearch[]>([]);

  const [params] = useSearchParams();
  const redirectUrl = params.get("redirect_uri");

  const statuses = getFineStatuses(i18n.t);

  const cars = carsApi.useCars({ limit: 100, fields: CAR_FIELDS }, aborter.current.signal);

  const carDriversApi = useCarDriversApi();
  const carSubsidiariesApi = useCarSubsidiariesApi();

  const carDriversAborter = useRef(new AbortController());
  const carDrivers = usersApi.useAdminUsers(
    { limit: 100, fields: USER_FIELDS },
    carDriversAborter.current.signal,
    undefined,
    undefined,
    carId ?? undefined,
  );

  useEffect(() => {
    if (cars.error || (carId && carDrivers.error)) {
      dispatch(showErrorNotification(i18n, cars.error ?? carDrivers.error));
    }
  }, [cars.error, carDrivers.error]);

  const defaultValues: Partial<FineFormBody> = {
    car: fine?.car,
    driver: fine?.driver,
    subsidiary: fine?.subsidiary,
    status: statuses.find((x) => x.value === (fine?.status ?? "new")),
    verbalNumber: fine?.verbalNumber,
    violationDate: fine?.violationDate,
    communicationDate: fine?.communicationDate,
    amount: fine?.amount,
    amountCurrency: currencies.find((x) => x.value === fine?.amountCurrency),
    points: fine?.points,
    accountant: fine?.accountant,
    hrReferent: fine?.hrReferent,
    note: fine?.note,
  };

  return (
    <Form<FineFormBody>
      defaultValues={defaultValues}
      submitText={i18n.t.common.save}
      disabled={isDisabled}
      onSubmit={() => onSubmit}
      itemCreationInfo={fine}
      secondaryButton={
        redirectUrl ? (
          <Button buttonProps={{ onClick: () => navigate(redirectUrl) }} type="secondary">
            {i18n.t.common.back}
          </Button>
        ) : undefined
      }
    >
      {({ register, formState: { errors }, control, reset, setValue, watch }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [carId, fine]);

        // like in the cost import: when car or violation date change, the driver and the subsidiary the car had on that date
        // are proposed (a saved fine keeps its values), both can still be changed
        const selectedCarId = carId || watch("car")?.id;
        const violationDate = watch("violationDate");
        const driverPeriods = carDriversApi.useAdminCarDrivers<(typeof DRIVER_PERIOD_FIELDS)[number]>(
          { fields: DRIVER_PERIOD_FIELDS },
          aborter.current.signal,
          selectedCarId || false,
        );
        const subsidiaryPeriods = carSubsidiariesApi.useCarSubsidiaries<(typeof SUBSIDIARY_PERIOD_FIELDS)[number]>(
          { fields: SUBSIDIARY_PERIOD_FIELDS },
          aborter.current.signal,
          selectedCarId,
        );

        // dates are "YYYY-MM-DD" strings, so they can be compared directly; a period ends the day the next one starts
        const coversDate = (period: { startDate: string; endDate?: string }) =>
          period.startDate <= violationDate && (!period.endDate || violationDate < period.endDate);

        useEffect(() => {
          if (!violationDate || (selectedCarId === fine?.car.id && violationDate === fine?.violationDate)) {
            return;
          }

          if (driverPeriods.data) {
            const driverPeriod = driverPeriods.data.items.find(coversDate);
            setValue("driver", driverPeriod?.driver);
          }

          if (subsidiaryPeriods.data) {
            const subsidiaryPeriod = subsidiaryPeriods.data.items.find(coversDate);
            setValue("subsidiary", subsidiaryPeriod?.subsidiary);
          }
        }, [selectedCarId, violationDate, driverPeriods.data, subsidiaryPeriods.data]);

        const amount = watch("amount");
        const amountCurrency = watch("amountCurrency");

        return (
          <>
            <FormGrid columns={2}>
              {/* Verbal number */}
              <FormField label={i18n.t.fines.verbalNumber} error={errors.verbalNumber}>
                {({ labelId, isInvalid }) => (
                  <Input
                    id={labelId}
                    isInvalid={isInvalid}
                    disabled={isDisabled || isLoading}
                    {...register("verbalNumber", { required: true, minLength: 1 })}
                  />
                )}
              </FormField>

              {/* Violation date */}
              <Controller
                name="violationDate"
                control={control}
                rules={{
                  required: true,
                  validate: (value) => {
                    if (!value) return i18n.t.common.invalidDate;
                    const d = new Date(value);
                    if (d.getFullYear() < 1000) return i18n.t.common.invalidDate;
                    return undefined;
                  },
                }}
                render={({ fieldState }) => (
                  <FormField label={i18n.t.fines.violationDate} error={fieldState.error}>
                    {({ isInvalid }) => (
                      <DatePicker
                        onChange={(v) => setValue("violationDate", v!)}
                        isInvalid={isInvalid}
                        value={watch("violationDate")}
                        disabled={isDisabled || isLoading}
                      />
                    )}
                  </FormField>
                )}
              />

              {/* Communication date */}
              <Controller
                name="communicationDate"
                control={control}
                rules={{
                  validate: (value) => {
                    if (!value) return undefined;
                    const d = new Date(value);
                    if (d.getFullYear() < 1000) return i18n.t.common.invalidDate;
                    return undefined;
                  },
                }}
                render={({ fieldState }) => (
                  <FormField label={i18n.t.fines.communicationDate} error={fieldState.error} isOptional>
                    {({ isInvalid }) => (
                      <DatePicker
                        onChange={(v) => setValue("communicationDate", v ?? undefined)}
                        isInvalid={isInvalid}
                        value={watch("communicationDate")}
                        disabled={isDisabled || isLoading}
                      />
                    )}
                  </FormField>
                )}
              />

              {/* First notification date: taken from the notifications log, read only */}
              <FormField label={i18n.t.fines.firstNotificationDate} isOptional hidden={!fine}>
                <Input
                  disabled
                  value={
                    fine?.firstNotificationDate ? formatDateNumeric(fine.firstNotificationDate, i18n.language) : ""
                  }
                />
              </FormField>

              {/* Car */}
              <Controller
                name="car"
                control={control}
                rules={{ required: !carId }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.cars.singular} error={fieldState.error} hidden={!!carId}>
                    {({ labelId, isInvalid }) => {
                      const searchTerm = useRef("");
                      const aborter = useRef(new AbortController());

                      const loadOptions = createLoadOptions<CarSearch, CarField>(aborter, searchTerm, carsApi.getCars, {
                        fields: CAR_FIELDS,
                      });

                      useEffect(() => {
                        loadOptions(searchTerm.current).then(setDefaultCarOptions);
                      }, []);

                      return (
                        <AsyncSelect
                          {...field}
                          inputId={labelId}
                          isDisabled={isDisabled || isLoading}
                          isLoading={isLoading}
                          defaultOptions={defaultCarOptions}
                          loadOptions={loadOptions}
                          placeholder={`${i18n.t.common.select}...`}
                          getOptionLabel={(x) =>
                            x.currentPlateNumber || x.lastPlateNumber || x.vehicleIdentificationNumber
                          }
                          getOptionValue={(x) => x.id}
                          styles={reactSelectStyles<{
                            id: string;
                            vehicleIdentificationNumber: string;
                            currentPlateNumber?: string;
                            lastPlateNumber?: string;
                          }>(isInvalid)}
                          menuPosition="fixed"
                        />
                      );
                    }}
                  </FormField>
                )}
              />

              {/* Driver */}
              <Controller
                name="driver"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.carDrivers.singular} error={fieldState.error}>
                    {({ labelId, isInvalid }) => {
                      const searchTerm = useRef("");
                      const aborter = useRef(new AbortController());

                      const loadOptions = createLoadOptions<UserSearch, UserField>(
                        aborter,
                        searchTerm,
                        usersApi.getAdminUsers,
                        {
                          fields: USER_FIELDS,
                        },
                      );

                      useEffect(() => {
                        loadOptions(searchTerm.current).then(setDefaultUserOptions);
                      }, []);

                      if (carId) {
                        // scoped to a known car: offer only its historic drivers, no need for async search
                        return (
                          <Select<UserSearch>
                            {...field}
                            inputId={labelId}
                            isDisabled={isDisabled || isLoading}
                            isLoading={isLoading || carDrivers.isLoading}
                            options={carDrivers.data?.items}
                            placeholder={`${i18n.t.common.select}...`}
                            getOptionLabel={(x) => x.fullName}
                            getOptionValue={(x) => x.id}
                            styles={reactSelectStyles<UserSearch>(isInvalid)}
                            menuPosition="fixed"
                          />
                        );
                      }

                      return (
                        <AsyncSelect
                          {...field}
                          inputId={labelId}
                          isDisabled={isDisabled || isLoading}
                          isLoading={isLoading}
                          defaultOptions={defaultUserOptions}
                          loadOptions={loadOptions}
                          placeholder={`${i18n.t.common.select}...`}
                          getOptionLabel={(x) => x.fullName}
                          getOptionValue={(x) => x.id}
                          styles={reactSelectStyles<UserSearch>(isInvalid)}
                          menuPosition="fixed"
                        />
                      );
                    }}
                  </FormField>
                )}
              />

              {/* Subsidiary */}
              <Controller
                name="subsidiary"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.subsidiaries.singular} error={fieldState.error}>
                    {({ labelId, isInvalid }) => {
                      const searchTerm = useRef("");
                      const aborter = useRef(new AbortController());

                      const loadOptions = createLoadOptions<SubsidiarySearch, SubsidiaryField>(
                        aborter,
                        searchTerm,
                        subsidiariesApi.getAdminSubsidiaries,
                        {
                          fields: SUBSIDIARY_FIELDS,
                        },
                      );

                      useEffect(() => {
                        loadOptions(searchTerm.current).then(setDefaultSubsidiaryOptions);
                      }, []);

                      return (
                        <AsyncSelect
                          {...field}
                          inputId={labelId}
                          isDisabled={isDisabled || isLoading}
                          isLoading={isLoading}
                          defaultOptions={defaultSubsidiaryOptions}
                          loadOptions={loadOptions}
                          placeholder={`${i18n.t.common.select}...`}
                          getOptionLabel={(x) => x.name}
                          getOptionValue={(x) => x.id}
                          styles={reactSelectStyles<SubsidiarySearch>(isInvalid)}
                          menuPosition="fixed"
                        />
                      );
                    }}
                  </FormField>
                )}
              />

              {/* Amount */}
              <FormField label={i18n.t.fines.amount} isOptional>
                {({ labelId }) => {
                  const isAmountCurrencyRequired = !!amount;

                  register("amountCurrency", { required: isAmountCurrencyRequired });

                  return (
                    <CostInput
                      isDisabled={isDisabled}
                      isLoading={isLoading}
                      id={labelId}
                      onlyCostNet
                      onSetCostNet={(v) => setValue("amount", v)}
                      onSetCurrency={(v) => setValue("amountCurrency", v)}
                      costNet={amount ?? null}
                      currency={amountCurrency ?? null}
                      isNetInvalid={!!errors.amount}
                      isCurInvalid={!!errors.amountCurrency}
                    />
                  );
                }}
              </FormField>

              {/* Points */}
              <FormField label={i18n.t.fines.points} error={errors.points} isOptional>
                {({ labelId, isInvalid }) => (
                  <Numeric
                    id={labelId}
                    value={watch("points")}
                    onBlur={(v) => setValue("points", v)}
                    decimalScale={0}
                    allowNegative={false}
                    disabled={isDisabled || isLoading}
                    isInvalid={isInvalid}
                  />
                )}
              </FormField>

              {/* Accountant */}
              <Controller
                name="accountant"
                control={control}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.fines.accountant} error={fieldState.error} isOptional>
                    {({ labelId, isInvalid }) => {
                      const searchTerm = useRef("");
                      const aborter = useRef(new AbortController());

                      const loadOptions = createLoadOptions<UserSearch, UserField>(
                        aborter,
                        searchTerm,
                        usersApi.getAdminUsers,
                        {
                          fields: USER_FIELDS,
                        },
                      );

                      return (
                        <AsyncSelect
                          {...field}
                          inputId={labelId}
                          isDisabled={isDisabled || isLoading}
                          isLoading={isLoading}
                          loadOptions={loadOptions}
                          placeholder={`${i18n.t.common.select}...`}
                          getOptionLabel={(x) => x.fullName}
                          getOptionValue={(x) => x.id}
                          styles={reactSelectStyles<UserSearch>(isInvalid)}
                          menuPosition="fixed"
                          isClearable
                        />
                      );
                    }}
                  </FormField>
                )}
              />

              {/* HR Referent */}
              <Controller
                name="hrReferent"
                control={control}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.fines.hrReferent} error={fieldState.error} isOptional>
                    {({ labelId, isInvalid }) => {
                      const searchTerm = useRef("");
                      const aborter = useRef(new AbortController());

                      const loadOptions = createLoadOptions<UserSearch, UserField>(
                        aborter,
                        searchTerm,
                        usersApi.getAdminUsers,
                        {
                          fields: USER_FIELDS,
                        },
                      );

                      return (
                        <AsyncSelect
                          {...field}
                          inputId={labelId}
                          isDisabled={isDisabled || isLoading}
                          isLoading={isLoading}
                          loadOptions={loadOptions}
                          placeholder={`${i18n.t.common.select}...`}
                          getOptionLabel={(x) => x.fullName}
                          getOptionValue={(x) => x.id}
                          styles={reactSelectStyles<UserSearch>(isInvalid)}
                          menuPosition="fixed"
                          isClearable
                        />
                      );
                    }}
                  </FormField>
                )}
              />

              {/* Status */}
              <Controller
                name="status"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.common.state} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Select<FineStatusType>
                        {...field}
                        inputId={labelId}
                        isDisabled={isDisabled || isLoading}
                        isLoading={isLoading}
                        options={statuses}
                        placeholder={`${i18n.t.common.select}...`}
                        getOptionLabel={(x) => x.label}
                        getOptionValue={(x) => x.value}
                        styles={reactSelectStyles<FineStatusType>(isInvalid)}
                        menuPosition="fixed"
                      />
                    )}
                  </FormField>
                )}
              />

              {/* Note */}
              <FormField label={i18n.t.common.notes} error={errors.note} isOptional fullWidth>
                {({ labelId, isInvalid }) => (
                  <Textarea
                    id={labelId}
                    isInvalid={isInvalid}
                    disabled={isDisabled || isLoading}
                    {...register("note", { minLength: 1 })}
                  />
                )}
              </FormField>
            </FormGrid>
          </>
        );
      }}
    </Form>
  );
};

export default FineForm;
